<template>
  <div
    :class="[
      'flex items-center',
      compact
        ? ''
        : isCampaignDonate
        ? 'justify-between px-4 py-3 sm:px-6 sm:py-4'
        : 'justify-between py-7 px-7',
      isCampaignDonate && !compact ? 'bg-campaign-donate' : '',
    ]"
  >
    <h1 v-if="isCampaignDonate && campaign" class="heading-campaign text-white">
      {{ campaign?.npo?.name || '' }} {{ campaign?.location || '' }}
    </h1>

    <div class="app-navbar">
      <Menu v-if="!$route.meta.hideNavbar && isLoggedIn" as="div" class="relative overflow-visible">
        <div class="flex shrink-0 items-center gap-2 overflow-visible sm:gap-2.5">
          <NotificationBell
            v-if="shouldShowNotificationBell"
            class="shrink-0"
            :isCampaignDonate="isCampaignDonate || coloredHeader"
          />

          <MenuButton
            :class="[
              'app-navbar-trigger',
              isCampaignDonate ? 'app-navbar-trigger--campaign' : '',
              coloredHeader && !isCampaignDonate ? 'app-navbar-trigger--header' : '',
            ]"
          >
            <span class="sr-only">Open user menu</span>
            <div class="app-navbar-trigger__avatar" :style="profilePictureStyle">
              <img
                v-if="userProfilePicture"
                :src="userProfilePicture"
                alt="Profile Picture"
                class="h-full w-full object-cover"
              />
              <span v-else>{{ initials }}</span>
            </div>
            <div v-if="compact" class="app-navbar-trigger__info">
              <span
                class="app-navbar-trigger__name"
                :class="{ 'app-navbar-trigger__name--campaign': isCampaignDonate || coloredHeader }"
              >
                {{ fullName }}
              </span>
              <span
                v-if="primaryRole"
                class="app-navbar-trigger__role"
                :class="{ 'app-navbar-trigger__role--campaign': isCampaignDonate || coloredHeader }"
              >
                {{ primaryRole }}
              </span>
            </div>
            <span
              v-else
              class="hidden max-w-[10rem] truncate text-sm text-gray-600 sm:inline lg:max-w-[14rem]"
              :class="{ 'text-white': isCampaignDonate }"
            >
              {{ fullName }}
            </span>
            <ChevronDownIcon
              class="app-navbar-trigger__chevron"
              :class="{
                'app-navbar-trigger__chevron--campaign': isCampaignDonate || coloredHeader,
              }"
              aria-hidden="true"
            />
          </MenuButton>
        </div>

        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <MenuItems class="app-navbar-menu">
            <div class="app-navbar-menu__header">
              <div class="app-navbar-menu__user">
                <div class="app-navbar-menu__avatar" :style="profilePictureStyle">
                  <img
                    v-if="userProfilePicture"
                    :src="userProfilePicture"
                    alt=""
                    class="h-full w-full object-cover"
                  />
                  <span v-else>{{ initials }}</span>
                </div>
                <div class="min-w-0">
                  <p class="app-navbar-menu__name">{{ fullName }}</p>
                  <p v-if="userEmail" class="app-navbar-menu__email">{{ userEmail }}</p>
                </div>
              </div>
              <div v-if="userGroups.length" class="app-navbar-menu__roles">
                <span
                  v-for="item in userGroups"
                  :key="item.name"
                  class="app-navbar-menu__role-badge"
                >
                  {{ item.name }}
                </span>
              </div>
            </div>

            <div class="mt-1 space-y-0.5">
              <MenuItem v-slot="{ active }">
                <RouterLink
                  to="/campaigns"
                  :class="['app-navbar-menu__item', { 'app-navbar-menu__item--active': active }]"
                >
                  <span class="app-navbar-menu__item-icon">
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                  </span>
                  Home
                </RouterLink>
              </MenuItem>

              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  :class="['app-navbar-menu__item', { 'app-navbar-menu__item--active': active }]"
                  @click="viewPicture"
                >
                  <span class="app-navbar-menu__item-icon">
                    <PhotoIcon class="h-4 w-4" aria-hidden="true" />
                  </span>
                  View Profile Pic
                </button>
              </MenuItem>

              <MenuItem v-if="!userProfilePicture" v-slot="{ active }">
                <label
                  :class="[
                    'app-navbar-menu__item cursor-pointer',
                    { 'app-navbar-menu__item--active': active },
                  ]"
                >
                  <span class="app-navbar-menu__item-icon">
                    <ArrowUpTrayIcon class="h-4 w-4" aria-hidden="true" />
                  </span>
                  Upload Profile Pic
                  <input type="file" class="hidden" accept="image/*" @change="onFileChange" />
                </label>
              </MenuItem>

              <MenuItem v-if="isFundraiser" v-slot="{ active }">
                <div
                  :class="['app-navbar-menu__stat', { 'app-navbar-menu__item--active': active }]"
                >
                  <span class="app-navbar-menu__stat-label">Total stops</span>
                  <span class="app-navbar-menu__stat-value">{{ totalStopCount }}</span>
                </div>
              </MenuItem>
            </div>

            <div class="app-navbar-menu__footer">
              <button type="button" class="app-navbar-menu__logout" @click="logout">
                <span class="app-navbar-menu__logout-icon">
                  <ArrowRightOnRectangleIcon class="h-4 w-4" aria-hidden="true" />
                </span>
                Logout
              </button>
            </div>
          </MenuItems>
        </transition>
      </Menu>

      <Teleport to="body">
        <div
          v-if="showViewer"
          class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Profile picture"
          @click.self="showViewer = false"
        >
          <div class="app-navbar-profile-modal">
            <img
              :src="userProfilePicture"
              alt="Profile Picture"
              class="mx-auto max-h-[70vh] rounded-xl object-contain"
            />
            <div class="mt-5 flex flex-col justify-center gap-2 sm:flex-row sm:flex-wrap">
              <label class="btn--secondary cursor-pointer px-3 py-1.5 text-xs">
                Change Profile Pic
                <input type="file" class="hidden" accept="image/*" @change="onFileChange" />
              </label>
              <BaseButton variant="danger" size="xs" @click="removePicture">
                Remove Profile Pic
              </BaseButton>
              <BaseButton variant="ghost" size="xs" @click="showViewer = false"> Close </BaseButton>
            </div>
          </div>
        </div>
      </Teleport>

      <nav v-if="!$route.meta.hideNavbar && !isLoggedIn" class="flex items-center">
        <RouterLink
          v-if="$route.name !== 'Home'"
          to="/"
          :class="[
            'app-navbar-guest-link',
            { 'app-navbar-guest-link--campaign': isCampaignDonate },
          ]"
        >
          Home
        </RouterLink>
      </nav>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {
  ArrowRightOnRectangleIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  HomeIcon,
  PhotoIcon,
} from '@heroicons/vue/24/outline'
import { UserApi } from '@/services/users/'
import { WorkedHoursApi } from '@/services/workingHours'
import NotificationBell from './NotificationBell.vue'

export default {
  name: 'NavMenu',
  components: {
    Menu,
    NotificationBell,
    MenuButton,
    MenuItem,
    MenuItems,
    ChevronDownIcon,
    HomeIcon,
    PhotoIcon,
    ArrowUpTrayIcon,
    ArrowRightOnRectangleIcon,
  },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    coloredHeader: {
      type: Boolean,
      default: false,
    },
    isCampaignDonate: {
      type: Boolean,
      default: false,
    },
    campaign: {
      type: Object,
      default: () => null,
    },
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const showViewer = ref(false)

    const user = computed(() => store.getters.user || {})
    const fullName = computed(() => user.value.fullName || '')
    const userEmail = computed(() => user.value.email || '')
    const userProfilePicture = computed(() => user.value.profilePicture)

    const initials = computed(() => {
      const names = fullName.value.trim().split(' ')
      const first = names[0]?.[0] || ''
      const last = names.length > 1 ? names[names.length - 1][0] : ''
      return (first + last).toUpperCase()
    })

    const profilePictureStyle = computed(() => {
      if (userProfilePicture.value) return {}
      const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6']
      const index = fullName.value.charCodeAt(0) % colors.length
      return {
        backgroundColor: colors[index],
      }
    })

    const isFundraiser = computed(() => store.getters.isFundraiser)
    const isCanvassingOrgAdmin = computed(() => store.getters.isCanvassingOrgAdmin)
    const isCanvassingOrgFieldDirector = computed(() => store.getters.isCanvassingOrgFieldDirector)
    const isGivarooStaff = computed(() => store.getters.isGivarooStaff)

    const shouldShowNotificationBell = computed(() => {
      return (
        isFundraiser.value ||
        isCanvassingOrgAdmin.value ||
        isCanvassingOrgFieldDirector.value ||
        isGivarooStaff.value
      )
    })

    const userGroups = computed(
      () => store.getters.userGroups?.map((name) => ({ name, visible: false })) || [],
    )

    const primaryRole = computed(() => userGroups.value[0]?.name || '')

    const isLoggedIn = computed(() => store.getters.isLoggedIn)
    const totalStopCount = ref(0)

    const logout = async () => {
      await store.dispatch('setUser', null)
      router.push({ name: 'Home' })
    }

    const viewPicture = () => {
      if (userProfilePicture.value) {
        showViewer.value = true
      }
    }

    const onFileChange = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = async () => {
        const base64 = reader.result
        try {
          await UserApi.csc.customUpdate({
            id: user.value.id,
            profilePicture: base64,
          })
          await store.dispatch('fetchUser')
        } catch (error) {
          console.error('Failed to upload profile picture:', error)
        }
      }

      reader.readAsDataURL(file)
    }

    const removePicture = async () => {
      try {
        await UserApi.csc.customUpdate({
          id: user.value.id,
          profilePicture: null,
        })
        await store.dispatch('fetchUser')
        showViewer.value = false
      } catch (error) {
        console.error('Failed to remove profile picture:', error)
      }
    }

    const fetchTotalStopCount = async () => {
      if (!isFundraiser.value || !isLoggedIn.value) return
      try {
        const response = await WorkedHoursApi.csc.getTotalStopCount()
        totalStopCount.value = response?.total_stop_count ?? 0
      } catch (error) {
        console.error('Failed to fetch total stop count:', error)
      }
    }

    onMounted(async () => {
      await fetchTotalStopCount()
    })

    return {
      fullName,
      userEmail,
      primaryRole,
      initials,
      profilePictureStyle,
      userGroups,
      isLoggedIn,
      logout,
      viewPicture,
      removePicture,
      showViewer,
      userProfilePicture,
      onFileChange,
      isFundraiser,
      shouldShowNotificationBell,
      totalStopCount,
    }
  },
}
</script>

<style scoped>
.bg-campaign-donate {
  background-color: rgb(4, 99, 117);
}

.heading-campaign {
  @apply font-extrabold text-2xl md:text-3xl tracking-tight;
  font-family: 'JungleFever';
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

@media (max-width: 640px) {
  .heading-campaign {
    @apply text-xl;
  }
}
</style>
